import { useState } from "react";
import {
  BackButton,
  Button,
  EmailField,
  InputText,
  PhoneNumberField,
  SecondaryButton,
} from "../../components";
import {
  Control,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from "react-hook-form";
import SwitchSelector from "react-switch-selector";
import { connect, useDispatch } from "react-redux";
import {
  clearUserSearchInfo,
  searchPlans,
  setUserSearchInfo,
} from "../../store/user/actions";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store/root-reducer";
import { useNavigate } from "react-router-dom";
import RouteNames from "../../routes/type";
import { UserScope } from "../../store/user/types";
import { isScopeLoading } from "../../store/scope-loading/helpers";

interface FormData {
  first_name: string;
  last_name: string;
  zip_code: string;
  email: string;
  phone_number: string;
  invoice_number: string;
  search_type: string;
}

function SearchByPersonalInfo({ data, loading }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control, handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: {
      first_name: "",
      last_name: "",
      zip_code: "",
      email: "",
      phone_number: "",
      invoice_number: "",
      search_type: "email",
      ...data,
    },
  });

  const onSubmit = (values) => {
    const searchTypeMap = {
      email: "search_plan_1",
      phone_number: "search_plan_2",
      invoice_number: "search_plan_3",
    };

    const apiBody = {
      search_type: searchTypeMap[values.search_type],
      name_first: values.first_name,
      name_last: values.last_name,
      email: values.email,
      phone_number: values.phone_number,
      invoice_number: values.invoice_number,
      zip_code: values.zip_code,
    };
    dispatch(
      setUserSearchInfo({
        search_type: values.search_type,
        first_name: apiBody.name_first,
        last_name: apiBody.name_last,
        email: apiBody.email,
        phone_number: apiBody.phone_number,
        invoice_number: apiBody.invoice_number,
        zip_code: apiBody.zip_code,
      })
    );
    dispatch(searchPlans(apiBody));
  };

  return (
    <div className="px-4">
      <div className="mb-8 mt-6">
        <BackButton
          onClick={() => {
            navigate(`/${RouteNames.SELECT_SEARCH_METHOD}`);
            dispatch(clearUserSearchInfo());
          }}
        />
      </div>
      <div className="mb-5">
        <p className="text-lg text-gray-800 font-semibold pb-1 text-center">
          {t("Please enter the information on your invoice to find your plan.")}
        </p>
        <p className="text-[#df6857] font-semibold pb-3 text-center">
          {t(
            "Please use the information from your retailer invoice or receipt for best results."
          )}
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8 flex flex-col gap-4">
          <InputText
            name="first_name"
            placeholder={t("John")}
            label="First Name"
            control={control}
            required
            inputProps={{
              autoFocus: true,
            }}
          />
          <InputText
            name="last_name"
            placeholder={t("Doe")}
            label="Last Name"
            control={control}
            required
          />
          <InputText
            name="zip_code"
            placeholder="000000"
            inputProps={{
              inputMode: "numeric",
            }}
            label="Home ZIP Code"
            control={control}
          />
          <SelectDataMethod
            setValue={setValue}
            control={control}
            watch={watch}
          />
        </div>
        <Button submit isLoading={loading}>
          {t("Submit")}
        </Button>
        <SecondaryButton
          onClick={() => navigate(`/${RouteNames.WARRANTY_NUMBER}`)}
          className="mt-3 text-gray-600 bg-gray-100"
        >
          {t("Try Warranty Number")}
        </SecondaryButton>
      </form>
    </div>
  );
}

interface SearchOptions {
  label: string;
  value: string;
}

const SelectDataMethod = ({
  setValue,
  control,
  watch,
}: {
  setValue: UseFormSetValue<FormData>;
  control: Control<FormData>;
  watch: UseFormWatch<FormData>;
}) => {
  const onChange = (newValue: any) => {
    setValue("search_type", newValue);
  };
  const search_type = watch("search_type");
  const { t } = useTranslation();

  const searchOptions: SearchOptions[] = [
    {
      label: t("Email"),
      value: "email",
    },
    {
      label: t("Phone"),
      value: "phone_number",
    },
    {
      label: t("Invoice"),
      value: "invoice_number",
    },
  ];
  const initialSelectedIndex = searchOptions.findIndex(
    ({ value }) => value === search_type
  );
  return (
    <div className="grid gap-1">
      <div className="h-12">
        <SwitchSelector
          onChange={onChange}
          options={searchOptions}
          selectionIndicatorMargin={0}
          initialSelectedIndex={initialSelectedIndex}
          backgroundColor={"#fff"}
          border={10}
          optionBorderRadius={0}
          selectedBackgroundColor={"#DBE1EB"}
          selectedFontColor={"#2D6CCA"}
          fontColor={"#222222"}
        />
      </div>
      {search_type === "email" && (
        <EmailField
          control={control}
          name={"email"}
          required
          shouldUnregister
        />
      )}
      {search_type === "phone_number" && (
        <PhoneNumberField
          control={control}
          name={"phone_number"}
          inputProps={{
            autoFocus: true,
          }}
          required
          shouldUnregister
        />
      )}
      {search_type === "invoice_number" && (
        <InputText
          control={control}
          name={"invoice_number"}
          placeholder={t("Invoice")}
          inputProps={{
            autoFocus: true,
          }}
          required
          shouldUnregister
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: state.user.planSearchInfo || {},
  loading: isScopeLoading(state.scopeLoading, UserScope),
});

export default connect(mapStateToProps)(SearchByPersonalInfo);
